import { useEffect, useMemo, useRef, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { CreateItem } from 'core-components/library/dropdown'
import { Input } from 'core-components/library/inputs'
import { useSource } from 'core-components/library/selects'
import { ValidateInput } from 'core-components/library/validation'
import { IDataProviderFilter, IFilter, Logic, Operator } from 'core-components/types'

import { BaseMultiSelect } from './BaseMultiSelect'
import { ICommonMultiselect } from './models/ICommonMultiselect'

import styles from './MultiSelect.module.scss'

export interface IMultiselectProps<T = IForm, R = string | number>
  extends ICommonMultiselect<T, R> {
  /**
   * Show create item button when text field not empty
   */
  allowCreate?: boolean
  /**
   * Handler for create item
   */
  onCreate?: (value: string) => void
  /**
   * Show select all items button
   */
  allowSelectAll?: boolean
}

export function MultiSelect<T = IForm, R = string | number>({
  value,
  source,
  label,
  caption,
  onChange,
  onCreate,
  disabled,
  onDataLoad,
  useRowCheckbox,
  allowCreate,
  allowSelectAll,
  onCheck,
  checkedValues,
  checkFn,
  valueDependencies,
  margin,
  maxListPageSize,
  classes,
  limit,
  renderFunction,
  useDrag,
  noFilterOnSelect,
  prepareInputConfig,
  hierarchyField,
  expanded,
  _popupVisible,
  InputComponent = Input,
  ValidateInputComponent = ValidateInput,
  closeOnSelect = true,
  beforeRemoveHandler,
  itemTemplate,
  itemClickCallback,
  filter,
}: IMultiselectProps<T, R>) {
  const [inputted, setInputted] = useState<string>('')
  const [currentValue, setCurrentValue] = useState<any[]>(value)
  const inputRef = useRef<HTMLInputElement>(null)

  const [filters, setFilters] = useState<IFilter[]>([])
  const { staticSource, wip, data } = useSource<T, R>(source, caption, undefined, onDataLoad)
  const showCreate = allowCreate && inputted !== ''
  const showSelectAllButton = allowSelectAll && data.length !== currentValue.length

  const filteringStaticSource = useMemo(() => {
    const data = staticSource.fetcher.getStaticData!()
    if (data.length) {
      return staticSource.clone()
    }
    return undefined
  }, [staticSource])

  const filteredSource = useMemo(() => {
    if (!value.length) {
      setCurrentValue([])
      return
    }
    filteringStaticSource?.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: source.keyField,
          operator: Operator.IN,
          value: value.filter((el) => el),
        },
      ],
    })

    return filteringStaticSource
  }, [filteringStaticSource, value])

  useEffect(() => {
    if (!useRowCheckbox && !noFilterOnSelect) {
      const filters = value.map((el) => {
        return {
          field: source.keyField,
          operator: Operator.NOTEQUAL,
          value: isNaN(+el) ? el : +el,
        }
      })

      setFilters(filters)
      if (filter) {
        const mergedFilter: IDataProviderFilter = {
          ...filter,
          filters: [...filter.filters, ...filters],
        }
        staticSource.setFilter(mergedFilter)
      } else {
        staticSource.setFilter({
          filters,
          logic: Logic.AND,
        })
      }

      if (maxListPageSize) {
        staticSource.setPaging({ pageSize: maxListPageSize, page: 0 })
      }
    }
  }, [value, staticSource, filter])

  const checkValues = (foundedValues: T[], allValues: string[]) => {
    return allValues.map((el: string) => {
      const founded = foundedValues.find((fnd) => '' + fnd[source.keyField] === el + '')
      if (!founded) {
        return { [caption]: el, [source.keyField]: el }
      }
      return {
        [caption]: founded[caption],
        [source.keyField]: founded[source.keyField],
      }
    })
  }

  const renderCreateButton = () => {
    if (!showCreate && !showSelectAllButton) {
      return <></>
    }
    return (
      <div>
        {showCreate ? (
          <CreateItem
            onClick={async () => {
              if (onCreate) {
                onCreate(inputted)
              }
              setInputted('')
            }}
          />
        ) : null}
        {!showCreate && showSelectAllButton && data.length ? (
          <div
            className={styles.selectAllButton}
            onClick={selectAll}
            data-testid={'selectAllButton'}
          >
            Select all items
          </div>
        ) : null}
      </div>
    )
  }

  useEffect(() => {
    filteredSource?.fetch().then((res) => {
      if (res.items.length !== 0) {
        const all = checkValues(res.items, value)
        setCurrentValue(all)
      } else {
        setCurrentValue(value.map((el) => ({ [caption]: el, [source.keyField]: el })))
      }
    })
  }, [filteredSource, value])

  const selectAll = () => {
    const allKeys = data
      .filter((el) => {
        if (itemClickCallback) {
          const result = itemClickCallback(el)
          if (typeof result === 'boolean') {
            return result
          }
        }
        return true
      })
      .map((el) => el[staticSource.keyField])
    onChange(allKeys, data)
    setInputted('')
    inputRef.current?.blur()
  }

  const onChangeInputValueHandler = (text: string) => {
    staticSource.setFilter({
      filters: text
        ? [
            ...filters,
            {
              field: caption,
              operator: Operator.CONTAINS,
              value: text,
            },
          ]
        : filters,
      logic: Logic.AND,
    })
  }

  return (
    <BaseMultiSelect<T, R>
      inputted={inputted}
      setInputted={setInputted}
      items={data}
      currentValue={currentValue}
      itemClickCallback={itemClickCallback}
      setCurrentValue={setCurrentValue}
      inputRef={inputRef}
      value={value}
      source={staticSource}
      itemTemplate={itemTemplate}
      label={label}
      caption={caption}
      onChange={onChange}
      disabled={disabled}
      useRowCheckbox={useRowCheckbox}
      onCheck={onCheck}
      checkedValues={checkedValues}
      checkFn={checkFn && !wip ? () => checkFn(value.join(',')) : null}
      valueDependencies={valueDependencies}
      margin={margin}
      maxListPageSize={maxListPageSize}
      classes={classes}
      limit={limit}
      renderFunction={renderFunction}
      useDrag={useDrag}
      noFilterOnSelect={noFilterOnSelect}
      prepareInputConfig={prepareInputConfig}
      hierarchyField={hierarchyField}
      expanded={expanded}
      _popupVisible={_popupVisible}
      InputComponent={InputComponent}
      ValidateInputComponent={ValidateInputComponent}
      closeOnSelect={closeOnSelect}
      wip={wip}
      popupHeaderWithoutSelection={renderCreateButton}
      popupHeaderAfterLines={renderCreateButton}
      onChangeInputValueHandler={onChangeInputValueHandler}
      beforeRemoveHandler={beforeRemoveHandler}
    />
  )
}
